import React, { useMemo, useCallback } from 'react';
import { useFeatureGating, FeatureGateProps } from '../Hooks/useFeatureGating';
import { Button, Card, Space, Typography, Spin, Tag } from 'antd';
import { LockOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { AppRoutes } from 'src/enums/ERoutes';

const { Text, Paragraph } = Typography;

interface FeatureGateComponentProps extends FeatureGateProps {
	variant?: 'overlay' | 'inline' | 'modal';
	size?: 'sm' | 'md' | 'lg';
	featureType?: 'boolean' | 'numeric';
	data?: any;
	elementIds?: string[];
	usageKey?: string;
	usageParser?: (data: any, key: string) => number;
	gated?: boolean;
	title?: string;
	message?: string;
	subText?: string;
	usageText?: string;
	icon?: React.ReactNode;
	iconColor?: string;
	backgroundColor?: string;
	borderColor?: string;
	horizontal?: boolean;
	headerTitle?: string;
}

// Extracted reusable components
const FeatureIcon: React.FC<{
	icon?: React.ReactNode;
	iconColor?: string;
	showLockIcon?: boolean;
	size?: 'sm' | 'md' | 'lg';
}> = ({ icon, iconColor, showLockIcon = true, size = 'md' }) => {
	if (!showLockIcon) {
		return null;
	}

	const iconSizes = {
		sm: { container: 'w-8 h-8', icon: '16px' },
		md: { container: 'w-12 h-12', icon: '20px' },
		lg: { container: 'w-16 h-16', icon: '24px' },
	};

	const { container, icon: iconSize } = iconSizes[size];

	return (
		<div
			className={`flex items-center justify-center ${container} rounded-full bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200 shadow-sm`}
		>
			{icon || <LockOutlined style={{ fontSize: iconSize, color: iconColor || '#6366f1' }} />}
		</div>
	);
};

const WarningIcon: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
	const iconSizes = {
		sm: { container: 'w-8 h-8', icon: '16px' },
		md: { container: 'w-12 h-12', icon: '20px' },
		lg: { container: 'w-16 h-16', icon: '24px' },
	};

	const { container, icon } = iconSizes[size];

	return (
		<div
			className={`flex items-center justify-center ${container} rounded-full bg-gradient-to-br from-orange-100 to-red-100 border-2 border-orange-200 shadow-md`}
		>
			<WarningOutlined style={{ fontSize: icon, color: '#f97316' }} />
		</div>
	);
};

const FeatureContent: React.FC<{
	title: string;
	message: string;
	subText?: string;
	usageText?: string;
	showUsage?: boolean;
}> = ({ title, message, subText, usageText, showUsage = false }) => (
	<div className="space-y-3">
		<Typography.Title level={3} style={{ margin: 0, color: '#3730a3' }}>
			{title}
		</Typography.Title>
		<Paragraph style={{ textAlign: 'center', margin: 0, fontSize: '14px', color: '#6b7280' }}>
			{message}
		</Paragraph>
		{subText && (
			<div className="flex items-center justify-center gap-2 text-xs text-gray-500">
				<InfoCircleOutlined />
				<Text type="secondary">{subText}</Text>
			</div>
		)}
		{showUsage && usageText && (
			<div className="bg-gradient-to-r from-orange-100 to-red-100 px-4 py-2 rounded-full border border-orange-200">
				<Tag color="orange" className="text-sm font-medium">
					{usageText}
				</Tag>
			</div>
		)}
	</div>
);

const OverlayVariant: React.FC<{
	children: React.ReactNode;
	className?: string;
	icon: React.ReactNode;
	content: React.ReactNode;
	actionButton: React.ReactNode;
	bgColor?: string;
	borderColor?: string;
	horizontal?: boolean;
}> = ({
	children,
	className,
	icon,
	content,
	actionButton,
	bgColor = 'from-blue-50 to-indigo-50',
	borderColor = 'border-blue-100',
	horizontal = false,
}) => (
	<div className={`relative ${className}`}>
		<div className="min-h-[500px] md:min-h-[300px]">{children}</div>
		<div className="absolute inset-0 bg-white/95 backdrop-blur-md rounded-xl border border-gray-200 shadow-lg flex flex-col items-center justify-center p-8">
			<div
				className={`bg-gradient-to-br ${bgColor} rounded-2xl p-8 ${
					horizontal ? 'max-w-4xl' : 'max-w-md'
				} text-center border ${borderColor} shadow-xl`}
			>
				{horizontal ? (
					<div className="flex items-center justify-center gap-8">
						<div className="flex-shrink-0">{icon}</div>
						<div className="flex-1 max-w-md">{content}</div>
						<div className="flex-shrink-0">{actionButton}</div>
					</div>
				) : (
					<Space direction="vertical" align="center" size="large">
						{icon}
						{content}
						{actionButton}
					</Space>
				)}
			</div>
		</div>
	</div>
);

const InlineVariant: React.FC<{
	children: React.ReactNode;
	className?: string;
	icon: React.ReactNode;
	content: React.ReactNode;
	actionButton: React.ReactNode;
	bgColor?: string;
	borderColor?: string;
	horizontal?: boolean;
}> = ({
	children,
	className,
	icon,
	content,
	actionButton,
	bgColor = 'from-blue-50 to-indigo-50',
	borderColor = 'border-blue-200',
	horizontal = false,
}) => (
	<div className={`space-y-4 ${className}`}>
		<div className={`bg-gradient-to-r ${bgColor} border ${borderColor} rounded-xl p-6 shadow-sm`}>
			{horizontal ? (
				<div className="flex items-center justify-between gap-4">
					<div className="flex items-center gap-3">
						{icon}
						<Typography.Title level={4} style={{ margin: 0, color: '#3730a3' }}>
							{content}
						</Typography.Title>
					</div>
					{actionButton}
				</div>
			) : (
				<Space direction="vertical" size="middle" className="w-full">
					<div className="flex items-center gap-3">
						{icon}
						<Typography.Title level={4} style={{ margin: 0, color: '#3730a3' }}>
							{content}
						</Typography.Title>
					</div>
					{actionButton}
				</Space>
			)}
		</div>
		{children}
	</div>
);

const ModalVariant: React.FC<{
	className?: string;
	icon: React.ReactNode;
	content: React.ReactNode;
	actionButton: React.ReactNode;
	bgGradient?: string;
	horizontal?: boolean;
}> = ({
	className,
	icon,
	content,
	actionButton,
	bgGradient = 'linear-gradient(135deg, #fff 0%, #f0f4ff 100%)',
	horizontal = false,
}) => (
	<Card
		className={`${className} shadow-2xl border-0`}
		style={{
			maxWidth: horizontal ? '800px' : '450px',
			margin: '0 auto',
			background: bgGradient,
			borderRadius: '16px',
		}}
	>
		<div className={`p-4 ${horizontal ? 'text-left' : 'text-center'}`}>
			{horizontal ? (
				<div className="flex items-center justify-center gap-6">
					<div className="flex-shrink-0">{icon}</div>
					<div className="flex-1 max-w-md">{content}</div>
					<div className="flex-shrink-0">{actionButton}</div>
				</div>
			) : (
				<Space direction="vertical" align="center" size="large">
					{icon}
					<div className="space-y-4">{content}</div>
					{actionButton}
				</Space>
			)}
		</div>
	</Card>
);

export const FeatureGate: React.FC<FeatureGateComponentProps> = ({
	featureKey,
	categoryKey,
	fallbackMessage,
	actionButton,
	children,
	showLockIcon = true,
	className = '',
	variant = 'overlay',
	featureType = 'boolean',
	data,
	elementIds = [],
	usageKey,
	usageParser,
	gated = false,
	title,
	message,
	subText,
	usageText,
	icon,
	iconColor,
	backgroundColor,
	borderColor,
	horizontal = false,
	headerTitle,
}) => {
	const dispatch = useAppDispatch();
	const {
		isFeatureEnabled,
		isFeatureUnlimited,
		getFeatureNumericValue,
		hasFeature,
		getCurrentPlan,
		isLoading,
	} = useFeatureGating();

	// Memoized feature state
	const featureState = useMemo(() => {
		const isEnabled = isFeatureEnabled(featureKey, categoryKey);
		const isUnlimited = isFeatureUnlimited(featureKey, categoryKey);
		const featureExists = hasFeature(featureKey, categoryKey);
		const currentPlan = getCurrentPlan();
		const isBasicPlan = currentPlan === 'basic';

		return { isEnabled, isUnlimited, featureExists, currentPlan, isBasicPlan };
	}, [featureKey, categoryKey, isFeatureEnabled, isFeatureUnlimited, hasFeature, getCurrentPlan]);

	const { isEnabled, isUnlimited, featureExists, isBasicPlan } = featureState;

	// Determine effective variant: if numeric feature is not enabled and variant is inline, switch to overlay
	const effectiveVariant = useMemo(() => {
		if (featureType === 'numeric' && !isEnabled && variant === 'inline') {
			return 'overlay';
		}
		return variant;
	}, [featureType, isEnabled, variant]);

	// Memoized action button handler
	const handleActionButtonClick = useCallback((action: 'upgrade' | 'contact') => {
		if (action === 'upgrade') {
			dispatch(openModal({ modalId: MODAL_IDS.SUBSCRIPTION_MODAL }));
		} else {
			window.open(
				`${import.meta.env.VITE_APP_BASE_URL}${AppRoutes.CONTACT_SALES}`,
				'_blank',
				'noreferrer',
			);
		}
	}, []);

	// Memoized action button renderer
	const renderActionButton = useCallback(() => {
		if (!actionButton) {
			return (
				<Button
					type="primary"
					onClick={() => handleActionButtonClick(isBasicPlan ? 'upgrade' : 'contact')}
					size="large"
					className="shadow-sm hover:shadow-md transition-all duration-200"
				>
					{isBasicPlan ? 'Upgrade' : 'Contact Sales'}
				</Button>
			);
		}

		const buttonType =
			actionButton.variant === 'outline'
				? 'default'
				: actionButton.variant === 'secondary'
					? 'default'
					: actionButton.variant || 'primary';

		return (
			<Button
				type={buttonType}
				onClick={actionButton.onClick}
				size="large"
				className="shadow-sm hover:shadow-md transition-all duration-200"
			>
				{actionButton.text}
			</Button>
		);
	}, [actionButton, isBasicPlan, handleActionButtonClick]);

	// Memoized element manipulation logic
	const manipulateElements = useCallback(
		(shouldDisable: boolean) => {
			if (!elementIds.length) {
				return;
			}

			elementIds.forEach((id) => {
				const elements = document.querySelectorAll(`[data-feature-gate-id="${id}"]`);
				elements.forEach((element) => {
					if (shouldDisable) {
						// Disable buttons
						if (element.tagName === 'BUTTON') {
							(element as HTMLButtonElement).disabled = true;
							element.classList.add('opacity-50', 'cursor-not-allowed');
						}
						// Disable other interactive elements
						if (
							element.tagName === 'INPUT' ||
							element.tagName === 'SELECT' ||
							element.tagName === 'TEXTAREA'
						) {
							(element as HTMLInputElement).disabled = true;
							element.classList.add('opacity-50', 'cursor-not-allowed');
						}
						// Add visual indicator for divs
						if (element.tagName === 'DIV') {
							element.classList.add('opacity-50', 'pointer-events-none');
						}
					} else {
						// Re-enable elements
						element.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
						if (
							element.tagName === 'BUTTON' ||
							element.tagName === 'INPUT' ||
							element.tagName === 'SELECT' ||
							element.tagName === 'TEXTAREA'
						) {
							(element as HTMLButtonElement | HTMLInputElement).disabled = false;
						}
					}
				});
			});
		},
		[elementIds],
	);

	// Memoized usage calculation
	const usageData = useMemo(() => {
		if (!data || !usageKey) {
			return { currentUsage: 0, hasReachedLimit: false };
		}

		let currentUsage = 0;
		if (usageParser) {
			currentUsage = usageParser(data, usageKey);
		} else {
			try {
				const parsedData =
					typeof data[usageKey] === 'string' ? JSON.parse(data[usageKey]) : data[usageKey];
				currentUsage = Array.isArray(parsedData) ? parsedData.length : 0;
			} catch (error) {
				console.warn('Failed to parse usage data:', error);
				currentUsage = 0;
			}
		}

		const limit = getFeatureNumericValue(featureKey, categoryKey);
		const hasReachedLimit = currentUsage >= limit;

		return { currentUsage, limit, hasReachedLimit };
	}, [data, usageKey, usageParser, featureKey, categoryKey, getFeatureNumericValue]);

	// Effect for element manipulation
	React.useEffect(() => {
		if (!featureExists) {
			return;
		}

		if (gated) {
			manipulateElements(true);
		} else if (featureType === 'numeric') {
			if (isUnlimited) {
				return;
			}
			manipulateElements(usageData.hasReachedLimit);
		} else if (featureType === 'boolean' && elementIds.length > 0) {
			manipulateElements(!isEnabled);
		}
	}, [
		featureExists,
		gated,
		featureType,
		isUnlimited,
		usageData.hasReachedLimit,
		manipulateElements,
		elementIds,
		isEnabled,
	]);

	// Helper function to add data attributes to children
	const addDataAttributesToChildren = useCallback(
		(children: React.ReactNode): React.ReactNode => {
			if (!elementIds.length) {
				return children;
			}

			return React.Children.map(children, (child) => {
				if (React.isValidElement(child)) {
					const childProps = child.props;
					const childId = childProps.id;

					// If this child has an ID that matches one of our elementIds, add the data attribute
					if (childId && elementIds.includes(childId)) {
						return React.cloneElement(child, {
							...childProps,
							'data-feature-gate-id': childId,
						});
					}

					// Recursively process nested children
					if (childProps.children) {
						return React.cloneElement(child, {
							...childProps,
							children: addDataAttributesToChildren(childProps.children),
						});
					}
				}
				return child;
			});
		},
		[elementIds],
	);

	// Memoized common props
	const commonProps = useMemo(() => {
		const defaultTitle = title || 'Feature Locked';
		const defaultMessage = message || 'This feature is currently unavailable.';
		const defaultSubText = subText || 'Please upgrade your plan to access this feature.';
		const defaultIcon = icon || (
			<LockOutlined style={{ fontSize: '20px', color: iconColor || '#6366f1' }} />
		);
		const defaultBgColor = backgroundColor || 'from-blue-50 to-indigo-50';
		const defaultBorderColor = borderColor || 'border-blue-200';

		return {
			defaultTitle,
			defaultMessage,
			defaultSubText,
			defaultIcon,
			defaultBgColor,
			defaultBorderColor,
		};
	}, [title, message, subText, icon, iconColor, backgroundColor, borderColor]);

	// Early returns
	if (!featureExists) {
		return <>{children}</>;
	}

	if (isLoading) {
		return (
			<div className={`feature-gate-loading ${className} flex items-center justify-center p-8`}>
				<div className="text-center">
					<Spin size="large" />
					<Text className="block mt-4 text-gray-500">Loading feature settings...</Text>
				</div>
			</div>
		);
	}

	// Gated feature rendering
	if (gated) {
		const {
			defaultTitle,
			defaultMessage,
			defaultSubText,
			defaultIcon,
			defaultBgColor,
			defaultBorderColor,
		} = commonProps;

		const content = (
			<FeatureContent title={defaultTitle} message={defaultMessage} subText={defaultSubText} />
		);

		const actionButtonElement = renderActionButton();

		if (effectiveVariant === 'overlay') {
			return (
				<OverlayVariant
					className={className}
					icon={
						<FeatureIcon icon={defaultIcon} iconColor={iconColor} showLockIcon={showLockIcon} />
					}
					content={content}
					actionButton={actionButtonElement}
					bgColor={defaultBgColor}
					borderColor={defaultBorderColor}
					horizontal={horizontal}
				>
					{addDataAttributesToChildren(children)}
				</OverlayVariant>
			);
		}

		if (effectiveVariant === 'inline') {
			return (
				<InlineVariant
					className={className}
					icon={
						<FeatureIcon
							icon={defaultIcon}
							iconColor={iconColor}
							showLockIcon={showLockIcon}
							size="sm"
						/>
					}
					content={defaultTitle}
					actionButton={
						<>
							<Paragraph style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
								{defaultMessage}
							</Paragraph>
							{defaultSubText && (
								<Paragraph style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
									{defaultSubText}
								</Paragraph>
							)}
							{actionButtonElement}
						</>
					}
					bgColor={defaultBgColor}
					borderColor={defaultBorderColor}
					horizontal={horizontal}
				>
					{addDataAttributesToChildren(children)}
				</InlineVariant>
			);
		}

		if (effectiveVariant === 'modal') {
			return (
				<ModalVariant
					className={className}
					icon={
						<FeatureIcon
							icon={defaultIcon}
							iconColor={iconColor}
							showLockIcon={showLockIcon}
							size="lg"
						/>
					}
					content={
						<>
							<Typography.Title level={2} style={{ margin: 0, color: '#3730a3' }}>
								{defaultTitle}
							</Typography.Title>
							<Paragraph
								style={{
									textAlign: horizontal ? 'left' : 'center',
									margin: 0,
									fontSize: '16px',
									color: '#6b7280',
								}}
							>
								{defaultMessage}
							</Paragraph>
							{defaultSubText && (
								<Paragraph
									style={{
										textAlign: horizontal ? 'left' : 'center',
										margin: 0,
										fontSize: '14px',
										color: '#9ca3af',
									}}
								>
									{defaultSubText}
								</Paragraph>
							)}
						</>
					}
					actionButton={actionButtonElement}
					horizontal={horizontal}
				/>
			);
		}

		return null;
	}

	// Handle numeric feature gating
	if (featureType === 'numeric') {
		const limit = getFeatureNumericValue(featureKey, categoryKey);

		// If unlimited (-1), don't gate anything
		if (isUnlimited) {
			return <>{addDataAttributesToChildren(children)}</>;
		}

		// If limit is 0 or feature is disabled, treat it like a boolean feature
		if (limit === 0 || !isEnabled) {
			const defaultMessage =
				fallbackMessage ||
				(isBasicPlan
					? 'This feature requires a Pro plan to be enabled.'
					: `To ${isEnabled ? 'increase limit on' : 'enable'} this feature, please contact sales to get custom offers`);
			const customTitle = headerTitle || (isEnabled ? 'Limit Reached' : 'Feature Locked');
			const customMessage = message || defaultMessage;
			const customSubText = subText || null;

			const content = (
				<FeatureContent title={customTitle} message={customMessage} subText={customSubText} />
			);

			const actionButtonElement = renderActionButton();

			if (effectiveVariant === 'overlay') {
				return (
					<OverlayVariant
						className={className}
						icon={<FeatureIcon showLockIcon={showLockIcon} />}
						content={content}
						actionButton={actionButtonElement}
						horizontal={horizontal}
					>
						{addDataAttributesToChildren(children)}
					</OverlayVariant>
				);
			}

			if (effectiveVariant === 'inline') {
				return (
					<InlineVariant
						className={className}
						icon={<FeatureIcon showLockIcon={showLockIcon} size="sm" />}
						content={customTitle}
						actionButton={
							<>
								<Paragraph style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
									{customMessage}
								</Paragraph>
								{customSubText && (
									<div className="flex items-center gap-2 text-xs text-gray-500">
										<InfoCircleOutlined />
										<Text type="secondary">{customSubText}</Text>
									</div>
								)}
								{actionButtonElement}
							</>
						}
						horizontal={horizontal}
					>
						{addDataAttributesToChildren(children)}
					</InlineVariant>
				);
			}

			if (effectiveVariant === 'modal') {
				return (
					<ModalVariant
						className={className}
						icon={<FeatureIcon showLockIcon={showLockIcon} size="lg" />}
						content={
							<>
								<Typography.Title level={2} style={{ margin: 0, color: '#3730a3' }}>
									{customTitle}
								</Typography.Title>
								<Paragraph
									style={{
										textAlign: horizontal ? 'left' : 'center',
										margin: 0,
										fontSize: '16px',
										color: '#6b7280',
									}}
								>
									{customMessage}
								</Paragraph>
								{customSubText && (
									<div className="flex items-center justify-center gap-2 text-sm text-gray-500">
										<InfoCircleOutlined />
										<Text type="secondary">{customSubText}</Text>
									</div>
								)}
							</>
						}
						actionButton={actionButtonElement}
						horizontal={horizontal}
					/>
				);
			}

			return null;
		}

		// If limit not reached, show children normally
		if (!usageData.hasReachedLimit) {
			return <>{addDataAttributesToChildren(children)}</>;
		}

		// Limit reached - show warning variants
		const customTitle = headerTitle || (isEnabled ? 'Limit Reached' : 'Feature Locked');
		const customMessage =
			message ||
			`You've reached your limit of ${usageData.limit} ${title?.toLowerCase() || usageKey?.toLowerCase()}. You can still view and edit existing items, but cannot create new ones.`;
		const customSubText = subText || null;
		const customUsageText = usageText || `${usageData.currentUsage} of ${usageData.limit} used`;

		const content = (
			<FeatureContent
				title={customTitle}
				message={customMessage}
				subText={customSubText}
				usageText={customUsageText}
				showUsage={true}
			/>
		);

		const actionButtonElement = renderActionButton();

		if (effectiveVariant === 'overlay') {
			return (
				<OverlayVariant
					className={className}
					icon={<WarningIcon size="md" />}
					content={content}
					actionButton={actionButtonElement}
					bgColor="from-orange-50 to-red-50"
					borderColor="border-orange-100"
					horizontal={horizontal}
				>
					{addDataAttributesToChildren(children)}
				</OverlayVariant>
			);
		}

		if (effectiveVariant === 'inline') {
			return (
				<InlineVariant
					className={className}
					icon={<WarningIcon size="sm" />}
					content={customTitle}
					actionButton={
						<>
							<Paragraph style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
								{customMessage}
							</Paragraph>
							{customSubText && (
								<div className="flex items-center gap-2 text-xs text-gray-500">
									<InfoCircleOutlined />
									<Text type="secondary">{customSubText}</Text>
								</div>
							)}
							<div className="flex items-center justify-between">
								<Tag color="orange" className="text-sm font-medium">
									{customUsageText}
								</Tag>
								{actionButtonElement}
							</div>
						</>
					}
					bgColor="from-orange-50 to-red-50"
					borderColor="border-orange-200"
					horizontal={horizontal}
				>
					{addDataAttributesToChildren(children)}
				</InlineVariant>
			);
		}

		if (effectiveVariant === 'modal') {
			return (
				<ModalVariant
					className={className}
					icon={<WarningIcon size="lg" />}
					content={
						<>
							<Typography.Title level={2} style={{ margin: 0, color: '#ea580c' }}>
								{customTitle}
							</Typography.Title>
							<Paragraph
								style={{
									textAlign: horizontal ? 'left' : 'center',
									margin: 0,
									fontSize: '16px',
									color: '#6b7280',
								}}
							>
								{customMessage}
							</Paragraph>
							{customSubText && (
								<div className="flex items-center justify-center gap-2 text-sm text-gray-500">
									<InfoCircleOutlined />
									<Text type="secondary">{customSubText}</Text>
								</div>
							)}
							<div className="bg-gradient-to-r from-orange-100 to-red-100 px-6 py-3 rounded-full border border-orange-200">
								<Tag color="orange" className="text-base font-semibold">
									{customUsageText}
								</Tag>
							</div>
						</>
					}
					actionButton={actionButtonElement}
					bgGradient="linear-gradient(135deg, #fff 0%, #fef7f0 100%)"
					horizontal={horizontal}
				/>
			);
		}

		return <>{children}</>;
	}

	// Handle boolean feature gating (existing logic)
	if (isEnabled) {
		return <>{children}</>;
	}

	const defaultMessage =
		fallbackMessage ||
		(isBasicPlan
			? 'This feature requires a Pro plan to be enabled.'
			: `To ${isEnabled ? 'increase limit on' : 'enable'} this feature, please contact sales to get custom offers`);
	const customTitle = headerTitle || (isEnabled ? 'Limit Reached' : 'Feature Locked');
	const customMessage = message || defaultMessage;
	const customSubText = subText || null;

	const content = (
		<FeatureContent title={customTitle} message={customMessage} subText={customSubText} />
	);

	const actionButtonElement = renderActionButton();

	if (effectiveVariant === 'overlay') {
		return (
			<OverlayVariant
				className={className}
				icon={<FeatureIcon showLockIcon={showLockIcon} />}
				content={content}
				actionButton={actionButtonElement}
				horizontal={horizontal}
			>
				{children}
			</OverlayVariant>
		);
	}

	if (effectiveVariant === 'inline') {
		return (
			<InlineVariant
				className={className}
				icon={<FeatureIcon showLockIcon={showLockIcon} size="sm" />}
				content={customTitle}
				actionButton={
					<>
						<Paragraph style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
							{customMessage}
						</Paragraph>
						{customSubText && (
							<div className="flex items-center gap-2 text-xs text-gray-500">
								<InfoCircleOutlined />
								<Text type="secondary">{customSubText}</Text>
							</div>
						)}
						{actionButtonElement}
					</>
				}
				horizontal={horizontal}
			>
				{children}
			</InlineVariant>
		);
	}

	if (effectiveVariant === 'modal') {
		return (
			<ModalVariant
				className={className}
				icon={<FeatureIcon showLockIcon={showLockIcon} size="lg" />}
				content={
					<>
						<Typography.Title level={2} style={{ margin: 0, color: '#3730a3' }}>
							{customTitle}
						</Typography.Title>
						<Paragraph
							style={{
								textAlign: horizontal ? 'left' : 'center',
								margin: 0,
								fontSize: '16px',
								color: '#6b7280',
							}}
						>
							{customMessage}
						</Paragraph>
						{customSubText && (
							<div className="flex items-center justify-center gap-2 text-sm text-gray-500">
								<InfoCircleOutlined />
								<Text type="secondary">{customSubText}</Text>
							</div>
						)}
					</>
				}
				actionButton={actionButtonElement}
				horizontal={horizontal}
			/>
		);
	}

	return null;
};

// Higher-order component for easier usage
export const withFeatureGate = <T extends React.ComponentType<any>>(
	WrappedComponent: T,
	featureKey: string,
	categoryKey?: string,
	options?: Partial<FeatureGateComponentProps>,
) => {
	const WrappedWithGate = (props: React.ComponentProps<T>) => (
		<FeatureGate featureKey={featureKey} categoryKey={categoryKey} {...options}>
			<WrappedComponent {...props} />
		</FeatureGate>
	);

	WrappedWithGate.displayName = `withFeatureGate(${WrappedComponent.displayName || WrappedComponent.name})`;
	return WrappedWithGate;
};

// Utility component for conditional rendering
export const ConditionalFeature: React.FC<{
	featureKey: string;
	categoryKey?: string;
	children: React.ReactNode;
	fallback?: React.ReactNode;
}> = ({ featureKey, categoryKey, children, fallback }) => {
	const { isFeatureEnabled } = useFeatureGating();

	if (isFeatureEnabled(featureKey, categoryKey)) {
		return <>{children}</>;
	}

	return <>{fallback}</>;
};
